import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table, Input, Tag, Select, Modal, message, Spin, Row, Col, Card, Dropdown, Badge,
} from "antd";
import {
  SearchOutlined,
  FilePdfOutlined,
  StopOutlined,
  TeamOutlined,
  VideoCameraOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MoreOutlined,
  BarsOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import axios from "axios";
import { API_CONST } from "../../../const";

const { Option } = Select;

const Wrapper = styled.div`
  .page-header {
    margin-bottom: 24px;
    h1 { font-size: 22px; font-weight: 700; color: #014c75; margin: 0 0 4px; }
    p { color: #666; font-size: 14px; margin: 0; }
  }
  .stat-card { border-radius: 8px; }
  .stat-inner { display: flex; align-items: center; gap: 14px; }
  .stat-icon {
    width: 44px; height: 44px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center; font-size: 20px; flex-shrink: 0;
  }
  .stat-info h3 { margin: 0; font-size: 22px; font-weight: 700; color: #014c75; line-height: 1.2; }
  .stat-info p { margin: 0; font-size: 12px; color: #888; }
  .filter-row {
    display: flex; align-items: center; gap: 12px; margin-bottom: 16px; flex-wrap: wrap;
  }
  .ant-table { background: white; border-radius: 8px; }
  .view-toggle-btn {
    display: inline-flex; align-items: center; justify-content: center;
    width: 34px; height: 34px; border-radius: 6px; border: 1px solid #d9d9d9;
    cursor: pointer; background: #fff; font-size: 16px; color: #595959;
    transition: all 0.2s;
    &:hover { border-color: #014c75; color: #014c75; }
    &.active { background: #014c75; border-color: #014c75; color: #fff; }
  }
`;

const KanbanBoard = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding-bottom: 16px;
  align-items: flex-start;
  min-height: 200px;
`;

const KanbanColumn = styled.div`
  flex: 0 0 270px;
  background: #f7f8fa;
  border-radius: 10px;
  border-top: 3px solid ${(p) => p.$color};
  padding: 12px;
  min-height: 160px;
`;

const KanbanColHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  .col-title {
    font-size: 13px;
    font-weight: 600;
    color: ${(p) => p.$color};
  }
`;

const KanbanCard = styled.div`
  background: #fff;
  border-radius: 8px;
  border: 1px solid #e8eaf0;
  padding: 12px;
  margin-bottom: 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  transition: box-shadow 0.15s;
  &:hover { box-shadow: 0 3px 10px rgba(0,0,0,0.09); }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 10px;
  gap: 6px;
`;

const CardName = styled.a`
  font-size: 13px;
  font-weight: 600;
  color: #014c75 !important;
  line-height: 1.3;
  flex: 1;
  &:hover { text-decoration: underline; }
`;

const CardField = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 5px;
  font-size: 12px;
  .field-label { color: #aaa; min-width: 68px; flex-shrink: 0; }
  .field-value { color: #3d3d3d; font-weight: 500; word-break: break-word; }
`;

const formatCurrency = (amount) => {
  if (!amount) return "-";
  return `₹ ${Number(amount).toLocaleString("en-IN")}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "2-digit" });
};

const STATUS_MAP = {
  submitted: "Under Review",
  interview_scheduled: "Interview Scheduled",
  hired: "Hired",
  rejected: "Rejected",
  withdrawn: "Withdrawn",
};

const STATUS_COLOR = {
  "Under Review": "blue",
  "Interview Scheduled": "orange",
  "Hired": "green",
  "Rejected": "red",
  "Withdrawn": "default",
};

const KANBAN_COLUMNS = [
  { key: "Under Review",        label: "Under Review",        color: "#1677ff", bg: "#e6f4ff" },
  { key: "Interview Scheduled", label: "Interview Scheduled", color: "#fa8c16", bg: "#fff7e6" },
  { key: "Hired",               label: "Hired",               color: "#52c41a", bg: "#f6ffed" },
  { key: "Rejected",            label: "Rejected",            color: "#ff4d4f", bg: "#fff2f0" },
  { key: "Withdrawn",           label: "Withdrawn",           color: "#8c8c8c", bg: "#f5f5f5" },
];

const PartnerSubmittedProfiles = () => {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [selectedJob, setSelectedJob] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [jobOptions, setJobOptions] = useState([]);
  const [viewMode, setViewMode] = useState("kanban");

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const userOrg = localStorage.getItem("userOrganization");

      const jobsRes = await axios.post(
        API_CONST.GET_JOB_REQUIREMENTS,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const jobs = jobsRes.data.Response || [];
      setJobOptions(jobs.map((j) => ({ id: j.id, title: j.role })));

      const allProfiles = [];
      await Promise.all(
        jobs.map(async (job) => {
          try {
            const appRes = await axios.post(
              API_CONST.GET_JOB_APPLICATIONS,
              { jobId: job.id, partnerOrganization: userOrg },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            const apps = appRes.data.applications || [];
            apps.forEach((app) => {
              allProfiles.push({
                key: app.applicationId,
                applicationId: app.applicationId,
                talentId: app.talentId,
                name: app.talent?.name || "Unknown",
                jobId: job.id,
                jobTitle: job.role,
                primarySkill: (app.talent?.primarySkills || []).filter(Boolean).join(", ") || "-",
                experience: app.talent?.experience || "-",
                monthlyRate: formatCurrency(app.talent?.monthlyRate),
                submittedOn: formatDate(app.submittedAt),
                status: STATUS_MAP[app.status] || app.status,
                rawStatus: app.status,
                resumeUrl: app.talent?.resumeUrl,
              });
            });
          } catch {
          }
        })
      );
      setProfiles(allProfiles);
    } catch {
      message.error("Failed to load submitted profiles");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const handleViewResume = async (record) => {
    if (!record.resumeUrl) {
      message.warning("No resume uploaded for this talent");
      return;
    }
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.post(
        API_CONST.GET_DOCUMENT_URL,
        { objectPath: record.resumeUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.downloadUrl) {
        window.open(res.data.downloadUrl, "_blank");
      }
    } catch {
      message.error("Failed to download resume");
    }
  };

  const handleWithdraw = (record) => {
    Modal.confirm({
      title: "Withdraw Profile",
      icon: <StopOutlined style={{ color: "#ff4d4f" }} />,
      content: (
        <div>
          <p>Are you sure you want to withdraw <strong>{record.name}</strong>'s profile from this job?</p>
          <div style={{ padding: "10px 14px", background: "#fff2f0", borderRadius: 6, border: "1px solid #ffccc7", marginTop: 10 }}>
            <p style={{ margin: 0, fontWeight: 500, fontSize: 13 }}>{record.jobTitle}</p>
            <p style={{ margin: "3px 0 0", color: "#888", fontSize: 12 }}>#{record.jobId}</p>
          </div>
        </div>
      ),
      okText: "Withdraw",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.delete(
            `${API_CONST.WITHDRAW_JOB_APPLICATION}/${record.applicationId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setProfiles((prev) =>
            prev.map((p) =>
              p.key === record.key
                ? { ...p, status: "Withdrawn", rawStatus: "withdrawn" }
                : p
            )
          );
          message.success(`${record.name}'s profile has been withdrawn`);
        } catch (err) {
          message.error(err.response?.data?.message || "Failed to withdraw profile");
        }
      },
    });
  };

  const getActionItems = (record) => {
    const items = [
      {
        key: "resume",
        label: "View Resume",
        icon: <FilePdfOutlined />,
        onClick: () => handleViewResume(record),
      },
    ];
    if (["submitted", "interview_scheduled"].includes(record.rawStatus)) {
      items.push({ type: "divider" });
      items.push({
        key: "withdraw",
        label: "Withdraw",
        icon: <StopOutlined />,
        danger: true,
        onClick: () => handleWithdraw(record),
      });
    }
    return items;
  };

  const columns = [
    {
      title: "Talent",
      dataIndex: "name",
      key: "name",
      width: 160,
      render: (text, record) => (
        <a
          href="#"
          style={{ color: "#014c75", fontWeight: 500 }}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/partner/talent-details/${record.talentId}`, {
              state: { from: "/partner/submitted-profiles", fromLabel: "Back to Submitted Profiles" },
            });
          }}
        >
          {text}
        </a>
      ),
    },
    {
      title: "Job ID",
      dataIndex: "jobId",
      key: "jobId",
      width: 110,
      render: (jobId) => (
        <a
          href="#"
          style={{ color: "#014c75", fontWeight: 500 }}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/partner/submit-profiles/${jobId}`);
          }}
        >
          {jobId}
        </a>
      ),
    },
    { title: "Job Role", dataIndex: "jobTitle", key: "jobTitle", width: 180 },
    { title: "Experience", dataIndex: "experience", key: "experience", width: 100 },
    { title: "Primary Skill", dataIndex: "primarySkill", key: "primarySkill", width: 220, ellipsis: true },
    { title: "Monthly Rate", dataIndex: "monthlyRate", key: "monthlyRate", width: 130 },
    { title: "Submitted On", dataIndex: "submittedOn", key: "submittedOn", width: 120 },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 160,
      render: (status) => (
        <Tag color={STATUS_COLOR[status] || "default"}>{status}</Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 70,
      fixed: "right",
      render: (_, record) => (
        <Dropdown menu={{ items: getActionItems(record) }} trigger={["click"]}>
          <MoreOutlined style={{ cursor: "pointer", fontSize: 18 }} />
        </Dropdown>
      ),
    },
  ];

  const filteredProfiles = profiles.filter((p) => {
    const search = searchText.toLowerCase();
    const matchesSearch =
      !search ||
      p.name?.toLowerCase().includes(search) ||
      p.jobTitle?.toLowerCase().includes(search) ||
      p.jobId?.toLowerCase().includes(search);
    const matchesJob = selectedJob === "all" || p.jobId === selectedJob;
    const matchesStatus = selectedStatus === "all" || p.status === selectedStatus;
    return matchesSearch && matchesJob && matchesStatus;
  });

  const counts = {
    total: profiles.length,
    underReview: profiles.filter((p) => p.rawStatus === "submitted").length,
    interviewScheduled: profiles.filter((p) => p.rawStatus === "interview_scheduled").length,
    hired: profiles.filter((p) => p.rawStatus === "hired").length,
    rejected: profiles.filter((p) => p.rawStatus === "rejected").length,
  };

  const statCards = [
    { label: "Total Submitted", value: counts.total, icon: <TeamOutlined />, bg: "#e6f4ff", color: "#1677ff" },
    { label: "Under Review", value: counts.underReview, icon: <FilePdfOutlined />, bg: "#e6f4ff", color: "#1677ff" },
    { label: "Interview Scheduled", value: counts.interviewScheduled, icon: <VideoCameraOutlined />, bg: "#fff7e6", color: "#fa8c16" },
    { label: "Hired", value: counts.hired, icon: <CheckCircleOutlined />, bg: "#f6ffed", color: "#52c41a" },
    { label: "Rejected", value: counts.rejected, icon: <CloseCircleOutlined />, bg: "#fff2f0", color: "#ff4d4f" },
  ];

  const renderKanban = () => (
    <KanbanBoard>
      {KANBAN_COLUMNS.map((col) => {
        const cards = filteredProfiles.filter((p) => p.status === col.key);
        return (
          <KanbanColumn key={col.key} $color={col.color}>
            <KanbanColHeader $color={col.color}>
              <span className="col-title">{col.label}</span>
              <Badge
                count={cards.length}
                style={{ backgroundColor: col.color, fontSize: 11, boxShadow: "none" }}
                showZero
              />
            </KanbanColHeader>
            {cards.length === 0 && (
              <div style={{ textAlign: "center", color: "#bbb", fontSize: 12, padding: "24px 0" }}>
                No profiles
              </div>
            )}
            {cards.map((record) => (
              <KanbanCard key={record.key}>
                <CardHeader>
                  <CardName
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/partner/talent-details/${record.talentId}`, {
                        state: { from: "/partner/submitted-profiles", fromLabel: "Back to Submitted Profiles" },
                      });
                    }}
                  >
                    {record.name}
                  </CardName>
                  <Dropdown menu={{ items: getActionItems(record) }} trigger={["click"]}>
                    <MoreOutlined
                      style={{ cursor: "pointer", fontSize: 16, color: "#888", flexShrink: 0, padding: "2px 4px" }}
                    />
                  </Dropdown>
                </CardHeader>
                <CardField>
                  <span className="field-label">Job ID</span>
                  <span className="field-value">
                    <a
                      href="#"
                      style={{ color: "#014c75" }}
                      onClick={(e) => { e.preventDefault(); navigate(`/partner/submit-profiles/${record.jobId}`); }}
                    >
                      {record.jobId}
                    </a>
                  </span>
                </CardField>
                <CardField>
                  <span className="field-label">Role</span>
                  <span className="field-value">{record.jobTitle}</span>
                </CardField>
                <CardField>
                  <span className="field-label">Experience</span>
                  <span className="field-value">{record.experience}</span>
                </CardField>
                <CardField>
                  <span className="field-label">Primary Skill</span>
                  <span className="field-value">{record.primarySkill}</span>
                </CardField>
                <CardField>
                  <span className="field-label">Monthly Rate</span>
                  <span className="field-value">{record.monthlyRate}</span>
                </CardField>
                <CardField>
                  <span className="field-label">Submitted</span>
                  <span className="field-value">{record.submittedOn}</span>
                </CardField>
              </KanbanCard>
            ))}
          </KanbanColumn>
        );
      })}
    </KanbanBoard>
  );

  return (
    <Wrapper>
      <div className="page-header">
        <h1>Submitted Profiles</h1>
        <p>Track the status of talent profiles submitted to client jobs</p>
      </div>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {statCards.map((s) => (
          <Col key={s.label} xs={24} sm={12} md={8} lg={4} xl={4}>
            <Card className="stat-card" size="small">
              <div className="stat-inner">
                <div className="stat-icon" style={{ background: s.bg, color: s.color }}>
                  {s.icon}
                </div>
                <div className="stat-info">
                  <h3>{s.value}</h3>
                  <p>{s.label}</p>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <div className="filter-row">
        <Input
          placeholder="Search talent, job role..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
          style={{ width: 240 }}
        />
        <Select
          value={selectedJob}
          onChange={setSelectedJob}
          style={{ width: 220 }}
          showSearch
          filterOption={(input, option) =>
            option?.children?.toLowerCase().includes(input.toLowerCase())
          }
        >
          <Option value="all">All Jobs</Option>
          {jobOptions.map((j) => (
            <Option key={j.id} value={j.id}>
              {j.title} (#{j.id})
            </Option>
          ))}
        </Select>
        <Select value={selectedStatus} onChange={setSelectedStatus} style={{ width: 180 }}>
          <Option value="all">All Statuses</Option>
          <Option value="Under Review">Under Review</Option>
          <Option value="Interview Scheduled">Interview Scheduled</Option>
          <Option value="Hired">Hired</Option>
          <Option value="Rejected">Rejected</Option>
          <Option value="Withdrawn">Withdrawn</Option>
        </Select>
        <span style={{ color: "#888", fontSize: 13, marginLeft: "auto" }}>
          {filteredProfiles.length} profile{filteredProfiles.length !== 1 ? "s" : ""}
        </span>
        <div style={{ display: "flex", gap: 6 }}>
          <span
            className={`view-toggle-btn${viewMode === "table" ? " active" : ""}`}
            onClick={() => setViewMode("table")}
            title="Table view"
          >
            <BarsOutlined />
          </span>
          <span
            className={`view-toggle-btn${viewMode === "kanban" ? " active" : ""}`}
            onClick={() => setViewMode("kanban")}
            title="Kanban view"
          >
            <AppstoreOutlined />
          </span>
        </div>
      </div>

      <Spin spinning={loading}>
        {viewMode === "table" ? (
          <Card bodyStyle={{ padding: 0 }}>
            <Table
              columns={columns}
              dataSource={filteredProfiles}
              rowKey="key"
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50"],
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} profiles`,
              }}
              scroll={{ x: 1250 }}
              locale={{ emptyText: loading ? " " : "No submitted profiles found" }}
            />
          </Card>
        ) : (
          renderKanban()
        )}
      </Spin>
    </Wrapper>
  );
};

export default PartnerSubmittedProfiles;
